import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { ModalController } from '@ionic/angular/standalone';

import { AlertModalContentErrorsComponent, AlertModalTypedErrorsComponent } from '../../../components';
import { GoogleTagEventType } from '../../../types';
import { getFormErrorsAsObject, getFormValidationErrors } from '../../../validators';
import { AnalyticsService } from '../../analytics/analytics.service';
import { MixpanelService } from '../../mixpanel/mixpanel.service';
import { GuardFinderError } from '../Domain/guard-finder-error';

/**
 * Service for handling and displaying errors throughout the application.
 *
 * This service provides methods for:
 * - Displaying error modals with typed errors (GuardFinderError)
 * - Displaying error modals with simple messages
 * - Tracking errors with analytics services
 * - Handling form validation errors
 */
@Injectable({
    providedIn: 'root'
})
export class ErrorService {
    /**
     * Creates an instance of ErrorService.
     *
     * @param analytics - Service for tracking analytics events
     * @param mixpanelService - Service for tracking events in Mixpanel
     * @param modalCtrl - Controller for creating and managing modals
     */
    constructor(
        private analytics: AnalyticsService,
        private mixpanelService: MixpanelService,
        private modalCtrl: ModalController
    ) {}

    /**
     * Displays a modal with typed errors (GuardFinderError[]).
     * This is the preferred method for showing errors in the application.
     *
     * @param errors - Array of GuardFinderError objects to display in the modal
     * @returns Promise that resolves when the modal is presented
     */
    async showTypedErrorsModal(errors: GuardFinderError[]): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: AlertModalTypedErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        await modal.present();
    }

    /**
     * Displays a modal with a single error message.
     * Converts the message to a GuardFinderError and uses the typed errors modal.
     *
     * @param message - Error message to display in the modal
     * @returns Promise that resolves when the modal is presented
     */
    async showErrorMessageModal(message: string): Promise<void> {
        const errors = [new GuardFinderError(message)];
        const modal = await this.modalCtrl.create({
            component: AlertModalTypedErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        await modal.present();
    }

    /**
     * Displays a modal with untyped errors.
     *
     * @deprecated Use showTypedErrorsModal with GuardFinderError objects instead
     * @param errors - Error object or array to display in the modal
     * @returns Promise that resolves when the modal is created
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    async showErrorsModal(errors: any) {
        const modal = await this.modalCtrl.create({
            component: AlertModalContentErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        modal.present();
    }

    /**
     * Processes API errors and logs them to analytics services.
     *
     * @deprecated Use showTypedErrorsModal with GuardFinderError objects from ApiClient instead
     * @param page - The page where the error occurred
     * @param data - The error data from the API
     * @param additionalDataObj - Additional data to include in the error tracking
     */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    apiErrors(page: string, data: any, additionalDataObj: { [key: string]: any } = {}) {
        console.log(data);
        const error = data['error'] ? data.error : data;

        let errorMessage = '';
        const isArray = Array.isArray(error);
        const isObject = error.constructor.name === 'Object';

        if (error.message) {
            errorMessage = error.message;
        } else if (error.statusText) {
            errorMessage = error.statusText;
        } else if (error) {
            errorMessage = error;
        }

        if (isArray || isObject) {
            for (const err in error) {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                const message = error[err].reduce((acc: any, msg: any) => (acc += `${msg} `), '');
                console.log(message);

                const gtmTag = {
                    event: 'Error',
                    event_category: page,
                    event_label: message
                };
                this.analytics.pushGTMTag(gtmTag);
            }
        } else {
            const gtmTag: GoogleTagEventType = {
                event: 'Error',
                event_category: page,
                event_label: errorMessage
            };
            this.analytics.pushGTMTag(gtmTag);
        }

        console.log(errorMessage);

        if (!isObject) {
            this.mixpanelService.sendEvent('Error', {
                page: page,
                Error: ['System error'],
                error: errorMessage,
                ...additionalDataObj
            });
        } else {
            this.mixpanelService.sendEvent('Error', {
                page: page,
                Error: ['System error'],
                ...error,
                ...additionalDataObj
            });
        }
    }

    /**
     * Processes form validation errors and logs them to analytics services.
     *
     * @deprecated Use GuardFinderError.createFromFormGroupErrors and showTypedErrorsModal instead
     * @param page - The page where the form validation error occurred
     * @param form - The FormGroup containing validation errors
     * @param additionalDataObj - Additional data to include in the error tracking
     */
    formErrors(page: string, form: FormGroup, additionalDataObj = null) {
        const additionalData = additionalDataObj ? additionalDataObj : {};

        const errorObj = {
            page: page,
            Error: ['User error'],
            ...getFormErrorsAsObject(form),
            ...additionalData
        };

        const formErrors = getFormValidationErrors(form);
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        formErrors.forEach((f: any) => {
            const gtmTag: GoogleTagEventType = {
                event: 'Error',
                event_category: page,
                event_label: f.control,
                error_type: 'User error'
            };
            this.analytics.pushGTMTag(gtmTag);
        });

        this.mixpanelService.sendEvent('Error', errorObj);
    }
}
